// doc-kit: the shared, classic building blocks for procurement list pages
// (page frame, expandable document card, items panel, ⋮ menu, document-flow launcher, project-link dialog).
import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, CalendarDays, MoreVertical } from "lucide-react";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { DocumentFlowDialog } from "@/components/DocumentFlowDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const timeOf = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }).toLowerCase() : "";
};

/** Breadcrumb, title, one-line description, and the page actions on the right. */
export function PageFrame({ crumbs, title, description, actions, testId, children }) {
  return (
    <div className="space-y-5" data-testid={testId}>
      <div className="space-y-2">
        <PageBreadcrumb crumbs={crumbs} />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">{title}</h1>
            {description && <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

/** One document as an expandable card. `meta` = [{ icon?, text }]; `right` = status, buttons and menu. */
export function DocCard({ testId, toggleId, open, onToggle, flat, number, date, time, meta = [], line, note, right, children }) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-3 p-4">
        <button type="button" className={`flex min-w-0 items-start gap-3 text-left ${flat ? "cursor-default" : ""}`} onClick={flat ? undefined : onToggle} aria-expanded={flat ? undefined : open} data-testid={toggleId}>
          {!flat && (open ? <ChevronDown className="mt-1 h-5 w-5 shrink-0 text-foreground" /> : <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-foreground" />)}
          <span className="min-w-0">
            <span className="block text-lg font-bold leading-tight text-foreground">{number}</span>
            <span className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
              <CalendarDays className="h-3.5 w-3.5" /> {date}
              {time && <><span aria-hidden>•</span>{time}</>}
              {meta.map((m, i) => <span key={i} className="inline-flex items-center gap-x-2"><span aria-hidden>•</span>{m}</span>)}
            </span>
            {line && <span className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground">{line}</span>}
          </span>
        </button>
        <div className="flex flex-wrap items-center justify-end gap-3" onClick={(e) => e.stopPropagation()}>{right}</div>
      </div>
      {note && <p className={`-mt-2 px-4 pb-3 text-sm text-sky-600 dark:text-sky-400 ${flat ? "" : "pl-12"}`}>{note}</p>}
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

/** Grey panel with a header row and one row per line. columns: [{ label, w?: "2fr", right?: bool }]; rows: [[node, ...]]. */
export function ItemsPanel({ columns, rows, testId, intro }) {
  const tpl = columns.map((c) => c.w || "1fr").join(" ");
  return (
    <div className="overflow-x-auto rounded-xl border border-border/70 bg-secondary/40 px-5 py-4" data-testid={testId}>
      {intro}
      <div className="min-w-[32rem]">
        <div className="grid gap-4 text-xs font-semibold text-muted-foreground" style={{ gridTemplateColumns: tpl }}>
          {columns.map((c) => <span key={c.label} className={c.right ? "text-right" : ""}>{c.label}</span>)}
        </div>
        <div className="mt-2 space-y-2">
          {rows.map((cells, i) => (
            <div key={i} className="grid gap-4 text-sm text-foreground" style={{ gridTemplateColumns: tpl }}>
              {cells.map((c, j) => <span key={j} className={`min-w-0 break-words tabular-nums ${columns[j]?.right ? "text-right" : ""}`}>{c}</span>)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** The ⋮ menu. items: [{ key, label, icon: Icon, onSelect, danger?, disabled?, testId? }] (falsy entries are skipped). */
export function DocMenu({ testId, items }) {
  const list = items.filter(Boolean);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-9 w-9" title="More" data-testid={testId}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[190px]">
        {list.length === 0 && <DropdownMenuItem disabled>No other actions</DropdownMenuItem>}
        {list.map((it) => {
          const Icon = it.icon;
          return (
            <DropdownMenuItem key={it.key} disabled={it.disabled} onSelect={it.onSelect} data-testid={it.testId} className={it.danger ? "text-rose-600 focus:text-rose-600" : ""}>
              {Icon && <Icon className="h-4 w-4" />} {it.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Opens the Document-flow dialog (with optional Cancel) from a menu item. Render `flowNode` once in the page. */
export function useDocFlow(onChanged) {
  const [flow, setFlow] = useState(null);
  const btn = useRef(null);
  useEffect(() => { if (flow) btn.current?.click(); }, [flow]);
  const flowNode = flow ? (
    <DocumentFlowDialog key={flow.n} docType={flow.docType} docId={flow.docId} cancelEndpoint={flow.cancelEndpoint} onCancelled={() => { setFlow(null); onChanged?.(); }}
      trigger={<button ref={btn} type="button" className="hidden" aria-hidden tabIndex={-1} />} />
  ) : null;
  return { openFlow: (docType, docId, cancelEndpoint) => setFlow({ docType, docId, cancelEndpoint, n: Date.now() }), flowNode };
}

/** "Which project is this for?" dialog used by documents whose project link can be changed. */
export function LinkProjectDialog({ doc, projects, onClose, onSave }) {
  const [value, setValue] = useState("none");
  useEffect(() => { setValue(doc?.project_id || "none"); }, [doc]);
  return (
    <Dialog open={!!doc} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="link-project-dialog">
        <DialogHeader><DialogTitle>Project for {doc?.number}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">The site this is for — its cost is booked to that project.</p>
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger data-testid="link-project-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
          <SelectContent><SelectItem value="none">Not linked</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(doc, value === "none" ? null : value)} data-testid="link-project-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const inr = (n) => (n === null || n === undefined || n === "" ? "—" : Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));   // a hidden rate arrives as null

/** Small label-over-value block used on the right of a card (amounts etc.). */
export function Figure({ label, value, strong, sub }) {
  return (
    <div className="min-w-[5.5rem] text-right">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={`tabular-nums ${strong ? "text-base font-bold text-foreground" : "text-sm text-foreground"}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}
